import type { Http } from '../http';
import { endpoints } from '../endpoints';
import type { RequestOptions } from '../types/common';
import type {
  RawApplyPlantSubscriptionResponse,
  RawCreatePlantSubscriptionRequest,
  RawSubscriptionCurrentResponse,
  RawSubscriptionCycleListParams,
  RawSubscriptionCycleListResponse,
  RawSubscriptionListResponse,
  RawSubscriptionSearchParams,
  RawUpdateSubscriptionRequest,
  RawUpdatePlantSubscriptionStatusRequest,
  RawUpdatePlantSubscriptionStatusResponse,
  RawUpdatePlantSubscriptionTicketRequest,
  RawUpdatePlantSubscriptionTicketResponse,
} from '../types/subscription';

export function createSubscriptionApi(http: Http) {
  return {
    /** GET /api/v2/subscriptions — 구독 목록 조회 */
    async search(
      params?: RawSubscriptionSearchParams,
      options?: RequestOptions,
    ): Promise<RawSubscriptionListResponse> {
      const res = await http.get<RawSubscriptionListResponse>(endpoints.subscription.search, {
        params,
        signal: options?.signal,
      });
      return res.data;
    },

    /**
     * GET /api/v2/plants/{plantId}/subscriptions — 발전소 구독 내역(사이클) 목록.
     * 페이지네이션만 지원(정렬 미지원). 응답 노드는 `plant`/`plantId` 없는 사이클 DTO — 목록
     * 검색(`search`)의 노드와 다르다.
     */
    async cyclesByPlant(
      plantId: number,
      params?: RawSubscriptionCycleListParams,
      options?: RequestOptions,
    ): Promise<RawSubscriptionCycleListResponse> {
      const res = await http.get<RawSubscriptionCycleListResponse>(
        endpoints.subscription.cyclesByPlant(plantId),
        { params, signal: options?.signal },
      );
      return res.data;
    },

    /**
     * GET /api/v2/plants/{plantId}/subscriptions/current — 발전소 현재 구독 단건.
     * 스웨거 명시 용도: 무통장입금 주문 **전** 결제 금액(`amountDetail`)·상품 유형(`subscriptionCode`)·
     * 과금용 RTU-인버터(`deviceInfo`) 확인. 구독 상태가 ACTIVE 가 아니면 주문 진행 불가.
     */
    async currentByPlant(
      plantId: number,
      options?: RequestOptions,
    ): Promise<RawSubscriptionCurrentResponse> {
      const res = await http.get<RawSubscriptionCurrentResponse>(
        endpoints.subscription.currentByPlant(plantId),
        { signal: options?.signal },
      );
      return res.data;
    },

    /**
     * POST /api/v2/plants/{plantId}/subscriptions — 발전소 구독하기(관리자 부여 / 영수증 연동).
     * 응답 body 는 스웨거 미선언 — 소비처는 body 의존 금지(캐시 무효화로 재조회).
     */
    async applyByPlant(
      plantId: number,
      data: RawCreatePlantSubscriptionRequest,
    ): Promise<RawApplyPlantSubscriptionResponse> {
      const res = await http.post<RawApplyPlantSubscriptionResponse>(
        endpoints.subscription.applyByPlant(plantId),
        data,
      );
      return res.data;
    },

    /**
     * PATCH /api/v2/plants/{plantId}/subscriptions — 구독 **마스터** 약정 기간·메모 수정.
     * 해지·취소 상태면 수정 불가, 활성 구독 없으면 409. 응답 body 스웨거 미선언 → void 취급.
     */
    async updateByPlant(plantId: number, data: RawUpdateSubscriptionRequest): Promise<void> {
      await http.patch<void>(endpoints.subscription.updateByPlant(plantId), data);
    },

    /**
     * DELETE /api/v2/plants/{plantId}/subscriptions/cancel — 구독 해지.
     * body 없음. 종료일은 유형별 정책(월구독=주기말/약정말, 그 외=즉시)으로 BE 가 산정 후 CANCELED 전이.
     * ⚠️ 월구독 자동결제 중단은 BE 미구현(응답 스키마는 불변 — 선적용 가능하다고 BE 명시).
     */
    async cancelByPlant(plantId: number): Promise<void> {
      await http.delete<void>(endpoints.subscription.cancelByPlant(plantId));
    },

    /** PUT /api/v2/plants/{plantId}/tickets/{ticketId} — 구독 ticket 약정 기간 수정 */
    async updateTicket(
      plantId: number,
      ticketId: number,
      data: RawUpdatePlantSubscriptionTicketRequest,
    ): Promise<RawUpdatePlantSubscriptionTicketResponse> {
      const res = await http.put<RawUpdatePlantSubscriptionTicketResponse>(
        endpoints.subscription.updateTicket(plantId, ticketId),
        data,
      );
      return res.data;
    },

    /** PUT /api/v2/plants/{plantId}/subscriptions/status — 발전소 구독 상태 변경 */
    async updateStatusByPlant(
      plantId: number,
      data: RawUpdatePlantSubscriptionStatusRequest,
    ): Promise<RawUpdatePlantSubscriptionStatusResponse> {
      const res = await http.put<RawUpdatePlantSubscriptionStatusResponse>(
        endpoints.subscription.updateStatusByPlant(plantId),
        data,
      );
      return res.data;
    },
  };
}
